import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_immobilier-nantes/artifacts/rfdvblj1_Logo%20Inter%27Med%20Immobilier.png";

export const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { href: "/", label: "Accueil" },
    { href: "/dispositifs", label: "Dispositifs Fiscaux" },
    { href: "/catalogue", label: "Nos Biens" },
    { href: "/simulateur", label: "Simulateur" },
    { href: "/accession-aidee", label: "Accession Aidée" },
    { href: "/contact", label: "Contact" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100" data-testid="navbar">
      <div className="container-custom">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <img src={LOGO_URL} alt="Inter'med Immobilier" className="h-10 w-auto" />
            <span className="hidden md:block font-heading text-base text-harbor font-semibold">
              Inter'med Immobilier
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`nav-link text-sm ${isActive(link.href) ? "active" : ""}`}
                data-testid={`nav-${link.href.replace("/", "") || "home"}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden md:flex items-center">
            <Link to="/simulateur">
              <Button className="bg-accent hover:bg-accent/90 text-white" data-testid="cta-simulateur">
                Simuler mon projet
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 top-16 bg-white z-40 animate-fade-in" data-testid="mobile-menu">
          <div className="flex flex-col p-6 gap-4">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-base py-3 border-b border-gray-100 ${
                  isActive(link.href) ? "text-harbor font-semibold" : "text-gray-700"
                }`}
                onClick={() => setMobileMenuOpen(false)}
                data-testid={`mobile-nav-${link.href.replace("/", "") || "home"}`}
              >
                {link.label}
              </Link>
            ))}
            <Link to="/simulateur" onClick={() => setMobileMenuOpen(false)}>
              <Button className="w-full mt-4 bg-accent hover:bg-accent/90 text-white" data-testid="mobile-cta">
                Simuler mon projet
              </Button>
            </Link>
            <div className="mt-6 space-y-3 text-gray-600 text-sm">
              <a href="tel:0749375868" className="flex items-center gap-3">
                <Phone size={16} className="text-harbor" />
                07 49 37 58 68
              </a>
              <a href="mailto:contact@intermed-immobilier.com" className="flex items-center gap-3">
                <Mail size={16} className="text-harbor" />
                contact@intermed-immobilier.com
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export const Footer = () => {
  return (
    <footer className="footer-section py-16" data-testid="footer">
      <div className="container-custom">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <img src={LOGO_URL} alt="Inter'med Immobilier" className="h-14 w-auto mb-4 brightness-0 invert" />
            <p className="text-gray-400 text-sm leading-relaxed">
              Spécialiste de l'investissement locatif et résidentiel dans l'immobilier neuf et ancien à Nantes.
            </p>
            <p className="text-gray-500 text-xs mt-4">
              SIRET: 942 124 348 00015
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-lg mb-4 text-white">Navigation</h4>
            <ul className="space-y-2">
              <li><Link to="/" className="footer-link text-sm">Accueil</Link></li>
              <li><Link to="/dispositifs" className="footer-link text-sm">Dispositifs Fiscaux</Link></li>
              <li><Link to="/simulateur" className="footer-link text-sm">Simulateur</Link></li>
              <li><Link to="/accession-aidee" className="footer-link text-sm">Accession Aidée</Link></li>
              <li><Link to="/contact" className="footer-link text-sm">Contact</Link></li>
            </ul>
          </div>

          {/* Dispositifs */}
          <div>
            <h4 className="font-heading text-lg mb-4 text-white">Dispositifs</h4>
            <ul className="space-y-2">
              <li><Link to="/dispositifs#pinel" className="footer-link text-sm">Pinel / Pinel+</Link></li>
              <li><Link to="/dispositifs#denormandie" className="footer-link text-sm">Denormandie</Link></li>
              <li><Link to="/dispositifs#jeanbrun" className="footer-link text-sm">Jeanbrun</Link></li>
              <li><Link to="/dispositifs#locavantages" className="footer-link text-sm">Loc'Avantages</Link></li>
              <li><Link to="/dispositifs#lmnp" className="footer-link text-sm">LMNP</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading text-lg mb-4 text-white">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-gray-400 text-sm">
                <MapPin size={18} className="text-clay mt-0.5 flex-shrink-0" />
                <span>25 Rue de l'Indre<br />44000 Nantes</span>
              </li>
              <li>
                <a href="tel:0749375868" className="flex items-center gap-3 text-gray-400 text-sm hover:text-clay transition-colors">
                  <Phone size={18} className="text-clay" />
                  07 49 37 58 68
                </a>
              </li>
              <li>
                <a href="mailto:contact@intermed-immobilier.com" className="flex items-center gap-3 text-gray-400 text-sm hover:text-clay transition-colors">
                  <Mail size={18} className="text-clay" />
                  contact@intermed-immobilier.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-500 text-sm">
          <p>&copy; {new Date().getFullYear()} Inter'med Immobilier. Tous droits réservés.</p>
        </div>
      </div>
    </footer>
  );
};

export const Layout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow pt-16">
        {children}
      </main>
      <Footer />
    </div>
  );
};

export default Layout;
